import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, onSnapshot, query, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { 
    Search, Edit3, Trash2, X, Save, Plus, PhoneCall, 
    UserCheck, Calendar, AlertCircle, DollarSign, Filter, 
    SortAsc, ChevronDown 
} from 'lucide-react';
import { notifySuccess, notifyError } from '../../utils/alerts';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const ListaEstudiantes = () => {
    const navigate = useNavigate();
    const [estudiantes, setEstudiantes] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('todos'); // todos, activos, vencidos
    const [orden, setOrden] = useState('vencimiento'); // nombre, vencimiento
    const [editModal, setEditModal] = useState(false);
    const [selectedChild, setSelectedChild] = useState(null);

    // 1. Cargar estudiantes en tiempo real
    useEffect(() => {
        const q = query(collection(db, "ninos"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setEstudiantes(docs);
        });
        return () => unsubscribe();
    }, []);

    // 2. Función para calcular días restantes
    const calcularStatus = (fechaPago) => {
        if (!fechaPago) return { dias: 0, color: 'text-gray-400', label: 'Sin fecha', isVencido: true };
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        const pago = new Date(`${fechaPago}T12:00:00`);
        const diferencia = pago - hoy;
        const dias = Math.round(diferencia / (1000 * 60 * 60 * 24));

        if (dias <= 0) return { num: dias, dias: 'Vencido', color: 'text-red-600 font-black', isVencido: true };
        if (dias <= 5) return { num: dias, dias: `${dias} días`, color: 'text-orange-500 font-bold', isVencido: false };
        return { num: dias, dias: `${dias} días`, color: 'text-green-600 font-bold', isVencido: false };
    };

    // 3. LÓGICA DE FILTRADO Y ORDENAMIENTO
    const filteredAndSorted = estudiantes
        .filter(e => {
            const matchesBusqueda = e.nombre.toLowerCase().includes(busqueda.toLowerCase()) || e.identificacion.includes(busqueda);
            const status = calcularStatus(e.proximaFechaPago);
            
            if (filtroEstado === 'activos') return matchesBusqueda && !status.isVencido;
            if (filtroEstado === 'vencidos') return matchesBusqueda && status.isVencido;
            return matchesBusqueda;
        })
        .sort((a, b) => {
            if (orden === 'vencimiento') {
                const statusA = calcularStatus(a.proximaFechaPago).num || -999;
                const statusB = calcularStatus(b.proximaFechaPago).num || -999;
                return statusA - statusB; // Los vencidos primero
            }
            return a.nombre.localeCompare(b.nombre);
        });

    const openEdit = (nino) => {
        setSelectedChild({ ...nino });
        setEditModal(true);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            const { id, ...dataToUpdate } = selectedChild;
            await updateDoc(doc(db, "ninos", id), dataToUpdate);
            setEditModal(false);
            notifySuccess("Actualizado", "Información actualizada correctamente.");
        } catch (error) {
            notifyError("Error", "No se pudo actualizar.");
        }
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: "Esta acción no se puede deshacer",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#10b981', // Verde esmeralda (Aceptar)
            cancelButtonColor: '#ef4444',  // Rojo (Cancelar)
            buttonsStyling: true,          // Asegura que use los estilos de Swal y no los del navegador
            customClass: {
                confirmButton: 'px-6 py-3 rounded-2xl font-black uppercase text-sm tracking-tight opacity-1 bg-[#10b981]',
                cancelButton: 'px-6 py-3 rounded-2xl font-black uppercase text-sm tracking-tight opacity-1 bg-[#ef4444]'
            },
        }).then(async (result) => {
            if (result.isConfirmed) {
                await deleteDoc(doc(db, "ninos", id));
                notifySuccess("Eliminado", "El registro ha sido borrado.");
            }
        });
    };

    return (
        <div className="space-y-4 sm:space-y-6 pb-20">
            <header className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 sm:gap-6">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-black text-gray-800 tracking-tight">Estudiantes</h1>
                    <p className="text-sm text-gray-500 font-medium">
                        Mostrando <span className="text-blue-600 font-bold">{filteredAndSorted.length}</span> resultados.
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
                    {/* BUSCADOR */}
                    <div className="relative flex-1 w-full sm:w-auto sm:min-w-[280px]">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar por nombre o ID..."
                            className="w-full pl-12 pr-4 py-3 bg-white border border-gray-100 rounded-xl sm:rounded-2xl shadow-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm font-medium"
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                        />
                    </div>

                    <div className="flex gap-3 w-full sm:w-auto">
                        {/* FILTRO ESTADO */}
                        <div className="relative flex-1 sm:flex-none">
                            <select 
                                value={filtroEstado}
                                onChange={(e) => setFiltroEstado(e.target.value)}
                                className="w-full appearance-none pl-10 pr-10 py-3 bg-white border border-gray-100 rounded-xl sm:rounded-2xl shadow-sm focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-600 text-[11px] sm:text-sm transition-all cursor-pointer"
                            >
                                <option value="todos">Todos</option>
                                <option value="activos">Activos</option>
                                <option value="vencidos">Vencidos</option>
                            </select>
                            <Filter className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-blue-500" size={16} />
                            <ChevronDown className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                        </div>

                        {/* ORDEN */}
                        <div className="relative flex-1 sm:flex-none">
                            <select 
                                value={orden}
                                onChange={(e) => setOrden(e.target.value)}
                                className="w-full appearance-none pl-10 pr-10 py-3 bg-white border border-gray-100 rounded-xl sm:rounded-2xl shadow-sm focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-600 text-[11px] sm:text-sm transition-all cursor-pointer"
                            >
                                <option value="vencimiento">A Vencer</option>
                                <option value="nombre">A-Z</option>
                            </select>
                            <SortAsc className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-blue-500" size={16} />
                            <ChevronDown className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                        </div>
                    </div>

                    <button
                        onClick={() => navigate('/admin/registrar')}
                        className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-black px-6 py-3 rounded-xl sm:rounded-2xl shadow-xl shadow-blue-100 transition-all active:scale-95 flex items-center justify-center gap-2"
                    >
                        <Plus size={18} strokeWidth={3} />
                        <span>Nuevo Estudiante</span>
                    </button>
                </div>
            </header>

            {/* TABLA */}
            <div className="bg-white rounded-[2rem] sm:rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto styled-scrollbar">
                    <table className="w-full text-left border-collapse min-w-[750px]">
                        <thead>
                            <tr className="bg-gray-50/50 border-b border-gray-100">
                                <th className="px-5 sm:px-8 py-4 sm:py-5 text-[10px] sm:text-xs font-black text-gray-400 uppercase tracking-widest leading-tight">Estudiante</th>
                                <th className="px-5 sm:px-8 py-4 sm:py-5 text-[10px] sm:text-xs font-black text-gray-400 uppercase tracking-widest text-center leading-tight">Matrícula</th>
                                <th className="px-5 sm:px-8 py-4 sm:py-5 text-[10px] sm:text-xs font-black text-gray-400 uppercase tracking-widest text-center leading-tight">Costo</th>
                                <th className="px-5 sm:px-8 py-4 sm:py-5 text-[10px] sm:text-xs font-black text-gray-400 uppercase tracking-widest leading-tight">Acudiente</th>
                                <th className="px-5 sm:px-8 py-4 sm:py-5 text-[10px] sm:text-xs font-black text-gray-400 uppercase tracking-widest text-right leading-tight">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {filteredAndSorted.map((nino) => {
                                const statusPago = calcularStatus(nino.proximaFechaPago);
                                return (
                                    <tr key={nino.id} className="hover:bg-blue-50/30 transition-colors group">
                                        <td className="px-5 sm:px-8 py-4 sm:py-5">
                                            <div className="flex items-center gap-3 sm:gap-4">
                                                <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center font-bold text-sm sm:text-base ${statusPago.isVencido ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                                                    {nino.nombre.charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-800 text-sm sm:text-base line-clamp-1">{nino.nombre}</p>
                                                    <p className="text-[10px] sm:text-xs text-gray-400 font-medium line-clamp-1">{nino.identificacion} • {nino.grado}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-5 sm:px-8 py-4 sm:py-5 text-center">
                                            <div className="flex flex-col items-center">
                                                <span className={`text-xs sm:text-sm whitespace-nowrap ${statusPago.color}`}>{statusPago.dias}</span>
                                                <span className="text-[9px] sm:text-[10px] text-gray-400 font-medium uppercase tracking-tighter whitespace-nowrap">
                                                    {nino.proximaFechaPago || 'N/A'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-5 sm:px-8 py-4 sm:py-5 text-center font-bold text-gray-700 text-sm sm:text-base">
                                            ${Number(nino.valorMensualidad || 0).toLocaleString('es-CO')}
                                        </td>
                                        <td className="px-5 sm:px-8 py-4 sm:py-5">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-bold text-gray-700 line-clamp-1">
                                                    <UserCheck size={14} className="text-blue-500 shrink-0" />
                                                    <span className="truncate max-w-[120px] sm:max-w-[180px]">{nino.acudientes?.[0]?.nombre || 'Sin datos'}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-400">
                                                    <PhoneCall size={12} className="shrink-0" />
                                                    {nino.acudientes?.[0]?.telefono || '---'}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-5 sm:px-8 py-4 sm:py-5 text-right">
                                            <div className="flex justify-end gap-1 sm:gap-2">
                                                <button onClick={() => openEdit(nino)} className="p-1.5 sm:p-2 text-blue-600 hover:bg-blue-100 rounded-lg sm:rounded-xl transition-colors">
                                                    <Edit3 size={16} className="sm:w-[18px] sm:h-[18px]" />
                                                </button>
                                                <button onClick={() => handleDelete(nino.id)} className="p-1.5 sm:p-2 text-red-400 hover:bg-red-50 rounded-lg sm:rounded-xl transition-colors">
                                                    <Trash2 size={16} className="sm:w-[18px] sm:h-[18px]" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredAndSorted.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-gray-400 font-medium">
                                        No se encontraron estudiantes.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MODAL DE EDICIÓN RESPONSIVO */}
            {editModal && selectedChild && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in" onClick={() => setEditModal(false)}>
                    <div className="bg-white w-full max-w-3xl rounded-t-[2.5rem] sm:rounded-[3rem] shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 sm:slide-in-from-bottom-0 sm:zoom-in-95" onClick={e => e.stopPropagation()}>
                        {/* Indicador de arrastre (solo móvil) */}
                        <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto mt-4 sm:hidden"></div>
                        
                        <div className="p-6 sm:p-8 border-b border-gray-100 flex justify-between items-center mt-2 sm:mt-0">
                            <div>
                                <h2 className="text-xl sm:text-2xl font-black text-gray-800 tracking-tight">Editar Estudiante</h2>
                                <p className="text-xs sm:text-sm text-gray-500 font-medium">{selectedChild.nombre}</p>
                            </div>
                            <button onClick={() => setEditModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors bg-gray-50">
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>

                        <form onSubmit={handleUpdate} className="p-6 sm:p-8 space-y-5 sm:space-y-6 max-h-[70vh] sm:max-h-[75vh] overflow-y-auto styled-scrollbar pb-10">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-wider">Nombre</label>
                                    <input className="w-full px-4 py-3 sm:p-4 bg-gray-50 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-bold border border-transparent focus:border-blue-200 focus:bg-white transition-all text-sm sm:text-base"
                                        value={selectedChild.nombre} onChange={e => setSelectedChild({ ...selectedChild, nombre: e.target.value })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-wider">ID Identificación</label>
                                    <input className="w-full px-4 py-3 sm:p-4 bg-gray-50 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-bold border border-transparent focus:border-blue-200 focus:bg-white transition-all text-sm sm:text-base"
                                        value={selectedChild.identificacion} onChange={e => setSelectedChild({ ...selectedChild, identificacion: e.target.value })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-wider">Grado</label>
                                    <input className="w-full px-4 py-3 sm:p-4 bg-gray-50 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-bold border border-transparent focus:border-blue-200 focus:bg-white transition-all text-sm sm:text-base"
                                        value={selectedChild.grado} onChange={e => setSelectedChild({ ...selectedChild, grado: e.target.value })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-green-500 uppercase ml-2 tracking-wider">Mensualidad ($)</label>
                                    <div className="relative">
                                        <DollarSign size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-green-500" />
                                        <input type="number" className="w-full px-4 py-3 sm:p-4 pl-10 sm:pl-10 bg-green-50/50 border border-green-100 rounded-2xl outline-none focus:ring-2 focus:ring-green-400 font-black text-green-700 text-sm sm:text-base transition-all focus:bg-white"
                                            value={selectedChild.valorMensualidad} onChange={e => setSelectedChild({...selectedChild, valorMensualidad: e.target.value})} />
                                    </div>
                                </div>
                                <div className="space-y-1 md:col-span-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-wider">Próxima Fecha de Pago</label>
                                    <input type="date" className="w-full px-4 py-3 sm:p-4 bg-gray-50 border border-transparent focus:border-blue-200 focus:bg-white rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-bold transition-all text-sm sm:text-base"
                                        value={selectedChild.proximaFechaPago} onChange={e => setSelectedChild({ ...selectedChild, proximaFechaPago: e.target.value })} />
                                </div>
                            </div>

                            <button type="submit" className="w-full bg-blue-600 text-white font-black py-4 sm:py-5 rounded-2xl sm:rounded-[2rem] shadow-xl shadow-blue-200 flex items-center justify-center gap-2 hover:bg-blue-700 hover:shadow-blue-300 transition-all active:scale-[0.98] mt-4">
                                <Save size={20} /> Guardar Cambios
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ListaEstudiantes;